/* Step 1 — Intake + auto-decline gate. Ported from the prototype's intakeBody().
   Funding-form data + pitch-deck, and the non-AI gate: pure field comparison, zero
   token spend. Pass enables AI analysis; fail (or a logged override) routes on.
   Data loads from GET /deal/:id/intake; override persists via POST .../intake/override. */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import DealStrip from '../../components/DealStrip';
import DeclineModal from '../../components/DeclineModal';
import { Loading, EmptyState, ErrorState } from '../../components/States';
import { useToast } from '../../components/Toast';
import { BASE } from '../../helpers';
import { fetchIntake, overrideGate, ok } from '../../api';

export default function Intake({ deal }) {
	const navigate = useNavigate();
	const showToast = useToast();
	const [declineOpen, setDeclineOpen] = useState(false);
	const [I, setI] = useState(null);
	const [error, setError] = useState(false);
	const [overriding, setOverriding] = useState(false);

	const load = useCallback(async () => {
		setError(false); setI(null);
		const data = await fetchIntake(deal.id);
		if (!data) { setError(true); return; }
		setI(data);
	}, [deal.id]);
	useEffect(() => { load(); }, [load]);

	if (error) return (<main className="page screen"><DealStrip deal={deal} stepId="intake" /><div style={{ marginTop: 14 }}><EmptyState title="No intake yet" sub="This deal has no funding-form intake recorded." /></div></main>);
	if (!I) return (<main className="page screen"><DealStrip deal={deal} stepId="intake" /><div style={{ marginTop: 14 }}><Loading label="Loading intake…" /></div></main>);

	const checks = I.checks || [];
	const form = I.form || [];
	const fired = checks.filter(c => !c.pass);
	const overridden = I.gateOutcome === 'overridden';

	const runAnalysis = () => {
		showToast(`Intelligentsia analysis started for ${deal.name}`);
		navigate(`${BASE}/deal/${deal.id}/analysis`);
	};
	const override = async () => {
		if (overriding) return;
		setOverriding(true);
		const res = await overrideGate(deal.id, 'Manual override — proceeding despite dealbreaker');
		setOverriding(false);
		if (!ok(res)) return;
		showToast(`Override logged — ${deal.name} proceeds despite dealbreaker (reason · who · when)`);
		setTimeout(() => navigate(`${BASE}/deal/${deal.id}/analysis`), 700);
	};

	return (
		<main className="page screen">
			<DealStrip deal={deal} stepId="intake" />

			<div className="grid grid--split45" style={{ marginTop: 14 }}>
				<div className="card">
					<div className="card__row" style={{ marginBottom: 12 }}><div className="card__label">From the funding form</div><span className="card__hint">Pre-populated · nothing re-typed</span></div>
					<dl className="fieldgrid">
						{form.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
					</dl>
				</div>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 12 }}>Pitch deck</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
						<span style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--dlg-fill)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>📄</span>
						<div>
							<div style={{ fontSize: 13.5, fontWeight: 500 }}>{I.deck || 'No deck uploaded'}</div>
							<div style={{ fontSize: 12.5, color: 'var(--dlg-green-ink)' }}>⟳ Pushed to Diligentsia data repository · autofill propagated</div>
						</div>
					</div>
					<div className="card__foot">Company account auto-created on Diligentsia · investment-readiness invite sent to the founder.</div>
				</div>
			</div>

			<div className="card">
				<div className="card__row" style={{ marginBottom: 6 }}>
					<div className="card__label">Auto-decline gate · {checks.length} criteria</div>
					<span className="card__hint">Checked against profile criteria · no AI analysis run</span>
				</div>
				{checks.map(c => (
					<div key={c.label} className={`gate__row ${c.pass ? '' : 'is-fail'}`}>
						<span className={`gate__mark ${c.pass ? 'gate__mark--pass' : 'gate__mark--fail'}`}>{c.pass ? '✓' : '✕'}</span>
						<span className="gate__label">{c.label}</span>
						<span style={{ fontWeight: 500 }}>{c.value}</span>
						<span className="gate__note">{c.note}</span>
					</div>
				))}

				{checks.length === 0
					? <div className="verdict verdict--pass"><span><strong>No rule-out criteria configured</strong> — applicant admitted. Sync your investor settings to enable the auto-decline gate. Continuing runs AI analysis (token spend starts here).</span></div>
					: fired.length === 0
						? <div className="verdict verdict--pass"><span><strong>Cleared the gate</strong> — all {checks.length} criteria match the profile. Continuing runs AI analysis (token spend starts here).</span></div>
						: <div className="verdict verdict--fail"><span><strong>Dealbreaker fired — {fired.length} of {checks.length}.</strong> {fired[0].label}: {fired[0].value}. No AI has run and no tokens were spent.</span></div>}

				{(fired.length === 0 || overridden)
					? (
						<div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
							<button className="pill pill--primary" onClick={runAnalysis}>Run AI analysis →</button>
							<button className="pill pill--ghost" onClick={() => setDeclineOpen(true)}>Decline with template email</button>
						</div>
					) : (
						<>
							<div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
								<button className="pill pill--primary" onClick={() => setDeclineOpen(true)}>Decline with template email →</button>
								<button className="pill pill--outline" onClick={override} disabled={overriding}>{overriding ? 'Logging…' : 'Proceed anyway'}</button>
							</div>
							<div style={{ fontSize: 12.5, color: 'var(--dlg-muted)', marginTop: 9 }}>Both paths are logged — reason, who, when — to the FCA-format decision log.</div>
						</>
					)}
			</div>

			{declineOpen && <DeclineModal deal={deal} context="gate" onClose={() => setDeclineOpen(false)} onDeclined={() => navigate(`${BASE}/pipeline`)} />}
		</main>
	);
}
